import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { btLayoutSidebarActions } from '@common-reducers/BtLayoutSidebarReducer';
import { backendApi, MyBridgeAgent } from '@common-reducers/backend-api/backend-api';
import { namespace } from '@common-config/constants';
import { Mascot } from '@common-components/mascot/Mascot';
import { AgentGlyph } from '@common-components/mascot/AgentGlyph';
import { toneForAgent } from '@common-components/mascot/tones';

type Props = {
    size?: number;
}

const digitsOf = (id: string): string => (id || '').replace(/[^\d]/g, '');

/** An enabled agent that answers this chat: listed in its chats, or not blocked when it answers all-except. */
export function agentServesChat(agent: MyBridgeAgent, chatDigits: string): boolean {
    if (!agent.enabled || !chatDigits) return false;
    if (agent.chatScope === 'all_except') {
        return !(agent.chatBlockList ?? []).some(c => digitsOf(c) === chatDigits);
    }
    return (agent.chatIds ?? []).some(c => digitsOf(c) === chatDigits);
}

export const ChatAgentButton = ({ size }: Props) => {
    const { t } = useTranslation(namespace);
    const dispatch = useAppDispatch();
    const { isOpen, componentToShow } = useAppSelector((state) => state.BtLayoutSidebarReducer);
    const currentChatId = useAppSelector((state) => state.WhatsAppReducer.currentChat?.id?._serialized ?? '');
    const teamInboxChatId = useAppSelector((state) => state.TeamInboxReducer?.chatViewMode?.chatId ?? '');
    // Same cached query the Quick Agents panel reads — no extra request per header.
    const { data: overview } = backendApi.useGetMyBridgeOverviewQuery();

    const isQuickAgentsOpen = isOpen && componentToShow === 'QuickAgents';

    // The face says whether an agent is on for THIS chat: awake in that agent's own
    // tone, or a colourless sleeping line face when none is.
    const activeAgent = useMemo(() => {
        const chatDigits = digitsOf(teamInboxChatId || currentChatId);
        return (overview?.agents ?? []).find(a => agentServesChat(a, chatDigits));
    }, [overview, currentChatId, teamInboxChatId]);

    const handleOpenButtonClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        if (isQuickAgentsOpen) {
            dispatch(btLayoutSidebarActions.closeSidebar());
            return;
        }

        dispatch(btLayoutSidebarActions.openWithComponent('QuickAgents'));
    };

    return (
        <TooltipProvider>
            <Tooltip>
                <TooltipTrigger asChild>
                    <button
                        type="button"
                        className={`p-1.5 rounded-full transition-colors mx-0.5 cursor-pointer relative hover:bg-gray-100 dark:hover:bg-gray-700 ${isQuickAgentsOpen ? 'text-blue-500 bg-blue-50 dark:bg-blue-900/30' : 'text-gray-500 dark:text-gray-400 hover:text-blue-500'}`}
                        onClick={handleOpenButtonClick}
                        aria-label={t('teamInbox.quickAgents.tooltip')}
                        data-agent-active={activeAgent ? 'true' : 'false'}
                    >
                        {activeAgent ? (
                            <Mascot size={size || 20} tone={toneForAgent(activeAgent.id)} mood="idle" level="ambient" />
                        ) : (
                            <AgentGlyph size={size || 20} asleep />
                        )}
                    </button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                    <p>{t('teamInbox.quickAgents.tooltip')}</p>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
};

export default React.memo(ChatAgentButton);
